import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Ban,
  Bookmark,
  Database,
  Info,
  Loader2,
  Play,
  RefreshCw,
  Save,
  ShieldAlert,
  Terminal,
} from 'lucide-react';
import { useRedirect } from '../../../Shared/Hooks/useRedirect ';
import { getDataConnections, getTableColumns, runConsole } from '../../Services/DataService';
import {
  ColumnMeta,
  DataConnection,
  DataQueryResult,
  ENVIRONMENT_STYLES,
} from '../../Types/DataServiceType';
import DataResultGrid, { CopyButton } from './DataResultGrid';
import { inferSingleTable, primaryKeyLabelsFor } from './recordLink';
import ProductionConfirmDialog from './ProductionConfirmDialog';

const SAMPLES = [
  {
    label: 'Recent rows',
    sql: 'SELECT *\nFROM your_table\nORDER BY created_at DESC\nLIMIT 100;',
  },
  {
    label: 'Column profile',
    sql: 'SELECT\n    column_a,\n    COUNT(*) AS row_count,\n    MIN(column_a) AS lowest,\n    MAX(column_a) AS highest\nFROM your_table\nGROUP BY column_a\nORDER BY row_count DESC\nLIMIT 50;',
  },
  {
    label: 'Join lookup',
    sql: 'SELECT parent.id, parent.name, child.name AS child_name\nFROM parent\nLEFT JOIN child ON child.parent_id = parent.id\nLIMIT 100;',
  },
];

const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition [color-scheme:dark] focus:border-cyan-500/50';

const labelClass = 'mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400';

const ConsoleComponent = () => {
  const redirect = useRedirect();
  const [searchParams] = useSearchParams();

  const [connections, setConnections] = useState<DataConnection[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [consoleColumns, setConsoleColumns] = useState<ColumnMeta[]>([]);
  const [sql, setSql] = useState('');
  const [limit, setLimit] = useState('1000');
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<DataQueryResult | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Arriving from history with a statement to re-run, or a connection to run it on.
  const prefillSql = searchParams.get('sql') ?? '';
  const prefillConnectionId = searchParams.get('connectionId') ?? '';

  useEffect(() => {
    if (prefillSql) setSql(prefillSql);
  }, [prefillSql]);

  useEffect(() => {
    if (prefillConnectionId) setConnectionId(prefillConnectionId);
  }, [prefillConnectionId]);

  const loadConnections = useCallback(async () => {
    setLoadingConnections(true);
    try {
      const response = await getDataConnections({}, 0, 100);
      if (response.success && response.data) {
        const items = (response.data.page?.content ?? []).filter((item) => item.allowConsole);
        setConnections(items);
        setConnectionId((current) => (current && items.some((i) => i.id === current) ? current : items[0]?.id ?? ''));
      }
    } catch (err) {
      console.error('Failed to load connections', err);
    } finally {
      setLoadingConnections(false);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const connection = useMemo(
    () => connections.find((item) => item.id === connectionId) ?? null,
    [connections, connectionId]
  );

  const isProduction = connection?.environment === 'PRODUCTION';
  const limitValue = Number(limit);
  const limitValid = Number.isInteger(limitValue) && limitValue >= 1 && limitValue <= 50000;

  const validate = (): string => {
    if (!connectionId) return 'Pick a connection first.';
    if (!sql.trim()) return 'Enter a statement.';
    if (!limitValid) return 'The limit must be a whole number between 1 and 50000.';
    return '';
  };

  const execute = async (confirmProductionAlias?: string) => {
    const response = await runConsole({
      connectionId,
      sql: sql.trim(),
      limit: limitValue,
      ...(confirmProductionAlias ? { confirmProductionAlias } : {}),
    });

    if (response.success && response.data) {
      setResult(response.data);
      setError('');
    } else {
      setError(response.message || 'The statement could not be run.');
      setResult(null);
    }
  };

  /**
   * Console results carry no declared table, so the only way to offer a
   * drill-down is to work out which table the statement read. That is only
   * offered when exactly one table appears, and the record page re-checks
   * access server-side regardless, so a misread here cannot widen anything.
   */
  const consoleTable = useMemo(
    () => inferSingleTable(result?.executedSql ?? ''),
    [result]
  );

  /**
   * The console has no table selected the way the builder does, so the key
   * columns have to be looked up for whichever table the statement turned out
   * to have read. A query result never carries key information of its own.
   */
  useEffect(() => {
    let cancelled = false;

    if (!connectionId || !consoleTable) {
      setConsoleColumns([]);
      return;
    }

    (async () => {
      const response = await getTableColumns(connectionId, consoleTable);
      if (cancelled) return;
      setConsoleColumns(response?.success && response.data ? response.data : []);
    })();

    return () => {
      cancelled = true;
    };
  }, [connectionId, consoleTable]);

  const primaryKeyLabels = useMemo(
    () => primaryKeyLabelsFor(consoleColumns, result?.columns ?? []),
    [consoleColumns, result]
  );

  const recordLink = useMemo(
    () => ({ connectionId, table: consoleTable ?? '', primaryKeyLabels }),
    [connectionId, consoleTable, primaryKeyLabels]
  );

  const handleRun = async () => {
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }

    setRunning(true);
    setError('');
    setResult(null);

    try {
      // Production always needs the alias typed out, so ask up front rather
      // than bouncing off the server's confirmation error.
      if (isProduction) {
        setConfirmOpen(true);
        return;
      }
      await execute();
    } catch (err) {
      console.error('Console statement failed', err);
      setError('The statement could not be run.');
    } finally {
      setRunning(false);
    }
  };

  /**
   * Hand the statement to the saved-query page through the URL, the same
   * hand-off the query builder uses, so the draft survives the navigation
   * without a global store.
   */
  const saveAsQuery = () => {
    if (!sql.trim()) {
      setError('Enter a statement before saving it.');
      return;
    }
    if (!connectionId) {
      setError('Pick a connection before saving this statement.');
      return;
    }
    const draft = {
      connectionId,
      rawSql: sql.trim(),
      limitValue: limitValid ? limitValue : 1000,
      description: 'Saved from the SQL console.',
    };
    redirect(`/admin/data-service/saved-queries?draft=${encodeURIComponent(JSON.stringify(draft))}`);
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-violet-500/30 bg-violet-500/10 text-violet-400">
            <Terminal size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">SQL Console</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Read-only statements, one at a time, checked against a keyword policy
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={loadConnections}
            disabled={loadingConnections}
            className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40 disabled:opacity-50"
          >
            <RefreshCw size={15} className={loadingConnections ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={saveAsQuery}
            disabled={!sql.trim()}
            title="Save this statement to Saved Queries"
            className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-300 transition hover:border-cyan-500/40 disabled:opacity-50"
          >
            <Save size={15} /> Save query
          </button>
          <button
            onClick={handleRun}
            disabled={running}
            className="flex items-center justify-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
          >
            {running ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />}
            {running ? 'Running' : 'Run'}
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-start gap-2 rounded-2xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-xs font-semibold text-amber-200">
        <ShieldAlert size={15} className="mt-0.5 flex-shrink-0" />
        <p>
          The console is restricted to single read statements. Schema changes, data-definition
          statements, file and network functions, and multi-statement scripts are rejected before
          they reach the database.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="md:col-span-2">
            <label className={labelClass}>Connection</label>
            <select
              value={connectionId}
              onChange={(e) => setConnectionId(e.target.value)}
              disabled={loadingConnections}
              className={fieldClass}
            >
              <option value="" className="bg-slate-900 text-slate-100">
                {loadingConnections
                  ? 'Loading…'
                  : connections.length === 0
                  ? 'No connection has the console enabled'
                  : 'Select a connection'}
              </option>
              {connections.map((item) => (
                <option key={item.id} value={item.id} className="bg-slate-900 text-slate-100">
                  {item.alias} ({item.environment})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Row limit</label>
            <input
              type="number"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              className={fieldClass}
            />
          </div>
        </div>

        {isProduction && (
          <div className="mt-3 flex items-center gap-2 rounded-xl border border-rose-500/30 bg-rose-500/5 px-3.5 py-2.5 text-[11px] font-black uppercase tracking-widest text-rose-300">
            <AlertTriangle size={13} /> {connection?.alias} is production. You will be asked to type
            the alias to continue.
          </div>
        )}

        <div className="mt-4">
          <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
            <label className={labelClass}>Statement</label>
            <div className="flex flex-wrap items-center gap-1.5">
              {SAMPLES.map((sample) => (
                <button
                  key={sample.label}
                  onClick={() => setSql(sample.sql)}
                  className="rounded-full border border-slate-800 bg-slate-950 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-400"
                >
                  {sample.label}
                </button>
              ))}
              <CopyButton text={sql} label="Copy" />
            </div>
          </div>

          <textarea
            rows={10}
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            spellCheck={false}
            placeholder={'SELECT column_a, column_b\nFROM your_table\nWHERE column_a = \'text\'\nLIMIT 100;'}
            className="w-full resize-y rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 font-mono text-xs leading-relaxed text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
          />

          {prefillSql && (
            <p className="mt-1.5 flex items-center gap-1.5 text-[10px] font-bold text-cyan-400">
              <Bookmark size={12} /> Loaded from history. Edit it here, or run it as it stands.
            </p>
          )}

          <p className="mt-1.5 flex items-center gap-1.5 text-[10px] font-bold text-slate-500">
            <Info size={12} /> The console takes no bind parameters, so write values as
            quoted literals. The statement is validated before execution.
          </p>
        </div>
      </div>

      <div className="mt-6">
      <DataResultGrid
        result={result}
        loading={running}
        error={error}
        canWrite={false}
        recordLink={recordLink}
        emptyMessage="Run a statement to see its result here."
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] font-bold text-slate-500">
        {connection && (
          <>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
                ENVIRONMENT_STYLES[connection.environment] ?? 'border-slate-700 bg-slate-950 text-slate-400'
              }`}
            >
              <Database size={11} /> {connection.environment}
            </span>
            <span>Timeout {connection.queryTimeoutSec}s</span>
            <span className="flex items-center gap-1">
              <Ban size={11} /> Row cap {connection.rowCap.toLocaleString()}
            </span>
          </>
        )}
        {connections.length === 0 && !loadingConnections && (
          <span className="text-amber-400">
            No connection has "allow SQL console" switched on. A super admin can enable it from the
            connection editor.
          </span>
        )}
      </div>

      <ProductionConfirmDialog
        isOpen={confirmOpen}
        alias={connection?.alias ?? ''}
        action="Running this statement against production"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={async (typedAlias) => {
          await execute(typedAlias);
          setConfirmOpen(false);
        }}
      />
    </div>
  );
};

export default ConsoleComponent;
